import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { createApiService, apiService } from "@/App";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import DeFiManager from "@/components/DeFiManager";
import Header from "@/components/Header";
import { 
  Bitcoin, 
  Bot, 
  ArrowLeft, 
  Shield, 
  Zap, 
  TrendingUp,
  Activity,
  RefreshCw,
  Play,
  Brain,
  Clock,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Settings,
  Coins
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

export default function AgentDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isConnected, user, connectWallet, getAuthHeaders } = useWallet();
  const authenticatedApi = createApiService(getAuthHeaders);
  const [agent, setAgent] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [defiPositions, setDefiPositions] = useState(null);
  const [activities, setActivities] = useState([]);
  const [yields, setYields] = useState([]);
  const [pegStatus, setPegStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [lastDecision, setLastDecision] = useState(null);
  const { showUsd: showYieldUsd, toggleUsd, usdRate } = useCurrency();
  const yieldAssetUsd = (assetUpper) => usdRate(assetUpper) || 0;

  const fetchData = async () => {
    try {
      const [agentRes, portfolioRes, activitiesRes, yieldsRes, pegRes] = await Promise.all([
        apiService.getAgent(id),
        apiService.getPortfolio(id),
        apiService.getActivities(id, 30),
        apiService.getYieldStrategies(),
        apiService.getPegStatus()
      ]);
      setAgent(agentRes.data);
      setPortfolio(portfolioRes.data);
      setActivities(activitiesRes.data);
      setYields(yieldsRes.data);
      setPegStatus(pegRes.data);
      
      // Fetch DeFi positions if user is authenticated
      if (isConnected) {
        try {
          const defiRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/agents/${id}/defi/positions`,
            { headers: getAuthHeaders() }
          );
          if (defiRes.ok) {
            const defiData = await defiRes.json();
            setDefiPositions(defiData);
          }
        } catch (defiErr) {
          console.log("Could not fetch DeFi positions (auth required)");
        }
      }
    } catch (err) {
      console.error("Failed to fetch agent data:", err);
      toast.error("Failed to load agent");
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Calculate dominant DeFi yield from positions
  const getDominantDefiYield = () => {
    if (!defiPositions?.positions?.length) return null;
    
    const yieldsByAsset = {};
    
    for (const pos of defiPositions.positions) {
      if (pos.status !== 'active') continue;
      
      const asset = (pos.asset || 'unknown').toLowerCase();
      const protocol = pos.protocol || 'unknown';
      const yieldEarned = pos.yield_earned || 0;
      const currentValue = pos.current_value || pos.amount_deposited || 0;
      
      if (!yieldsByAsset[asset]) {
        yieldsByAsset[asset] = { totalYield: 0, totalValue: 0, protocol };
      }
      
      yieldsByAsset[asset].totalYield += yieldEarned;
      yieldsByAsset[asset].totalValue += currentValue;
    }
    
    // Find dominant asset by value
    let dominant = null;
    let maxValue = 0;
    
    for (const [asset, data] of Object.entries(yieldsByAsset)) {
      if (data.totalValue > maxValue) {
        maxValue = data.totalValue;
        dominant = { asset, ...data };
      }
    }
    
    if (!dominant) return null;
    
    // Determine decimals (STXOSHI, STX, VELAR = 6; sBTC, ALEX = 8)
    const decimals = ['stxoshi', 'stx', 'velar', 'ststx'].includes(dominant.asset) ? 1_000_000 : 100_000_000;
    
    return {
      asset: dominant.asset.toUpperCase(),
      protocol: dominant.protocol,
      yield: dominant.totalYield / decimals,
      value: dominant.totalValue / decimals
    };
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isConnected]);

  const handleSimulateCycle = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }
    setSimulating(true);
    try {
      const res = await authenticatedApi.simulateCycle(id);
      toast.success(`Cycle complete! Generated ${res.data.activities_generated} activities`);
      setLastDecision(res.data.decision);
      fetchData();
    } catch (err) {
      console.error("Simulation error:", err);
      toast.error(err?.response?.data?.detail || "Simulation failed - please try again");
    } finally {
      setSimulating(false);
    }
  };

  const handleRequestDecision = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }
    setDeciding(true);
    try {
      const res = await authenticatedApi.requestDecision(id, aiContext || null);
      setLastDecision(res.data);
      toast.success("AI decision received");
      fetchData();
    } catch (err) {
      console.error("Decision error:", err);
      toast.error(err?.response?.data?.detail || "Failed to get AI decision");
    } finally {
      setDeciding(false);
    }
  };

  const handleRefreshBalances = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet first");
      return;
    }
    setRefreshing(true);
    try {
      const res = await authenticatedApi.refreshBalances(id);
      toast.success(`Balances refreshed: ${res.data.stx_balance.toFixed(2)} STX, ${res.data.sbtc_balance.toFixed(8)} sBTC`);
      fetchData();
    } catch (err) {
      console.error("Refresh error:", err);
      toast.error(err?.response?.data?.detail || "Failed to refresh balances");
    } finally {
      setRefreshing(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      } else {
        // Fallback for older browsers or restricted contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
        toast.success("Copied to clipboard");
      }
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy - please copy manually");
    }
  };

  const COLORS = ['#FF9F43', '#00F0FF', '#9D4EDD', '#00E676'];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'transaction': return <ArrowUpRight className="w-4 h-4" />;
      case 'yield': return <TrendingUp className="w-4 h-4" />;
      case 'decision': return <Zap className="w-4 h-4" />;
      case 'alert': return <Shield className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'transaction': return 'text-primary bg-primary/10';
      case 'yield': return 'text-green-400 bg-green-400/10';
      case 'decision': return 'text-secondary bg-secondary/10';
      case 'alert': return 'text-yellow-400 bg-yellow-400/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-6 h-6 text-primary animate-spin" />
          <span className="font-mono text-muted-foreground">Loading agent...</span>
        </div>
      </div>
    );
  }

  if (!agent) return null;

  // Mock portfolio history
  const portfolioHistory = [
    { time: "Day 1", value: agent.sbtc_balance * 0.95 },
    { time: "Day 2", value: agent.sbtc_balance * 0.97 },
    { time: "Day 3", value: agent.sbtc_balance * 0.96 },
    { time: "Day 4", value: agent.sbtc_balance * 0.99 },
    { time: "Day 5", value: agent.sbtc_balance * 1.01 },
    { time: "Day 6", value: agent.sbtc_balance * 1.02 },
    { time: "Today", value: agent.sbtc_balance },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Agent Info Bar */}
      <div className="glass px-3 md:px-6 py-3 border-b border-border">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <h1 className="font-heading font-bold text-sm md:text-lg">{agent.name}</h1>
            <span className="text-xs text-muted-foreground font-mono">{agent.config?.archetype}</span>
          </div>
          <div className="flex items-center gap-2">
            {agent.is_real_wallet && (
              <Badge variant="outline" className="font-mono text-xs border-green-500/50 text-green-400">
                MAINNET
              </Badge>
            )}
            <Badge 
              variant="outline" 
              className={`font-mono text-xs ${agent.status === 'active' ? 'border-green-500/50 text-green-400' : 'border-yellow-500/50 text-yellow-400'}`}
            >
              <span className={`w-2 h-2 rounded-full mr-2 ${agent.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'} pulse-live`}></span>
              {agent.status?.toUpperCase()}
            </Badge>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-3 md:px-6 py-3 border-b border-border bg-background/50">
        <div className="max-w-[1800px] mx-auto flex items-center gap-2">
          <Button 
            onClick={handleSimulateCycle}
            disabled={simulating}
            size="sm"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="run-cycle-btn"
          >
            {simulating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Cycle
              </>
            )}
          </Button>
          {agent.is_real_wallet && (
            <Button 
              onClick={handleRefreshBalances}
              disabled={refreshing}
              variant="outline"
              size="sm"
              data-testid="refresh-balances-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Balances
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-[1800px] mx-auto">
          {/* Top Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="glass-card" data-testid="agent-sbtc-balance">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">sBTC Balance</span>
                  <Bitcoin className="w-4 h-4 text-primary" />
                </div>
                <p className="font-mono text-2xl font-bold">{agent.sbtc_balance?.toFixed(8)}</p>
                <p className="text-xs text-muted-foreground font-mono">≈ ${portfolio?.total_value_usd?.toLocaleString() || '0'}</p>
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="agent-stx-balance">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">STX Reserve</span>
                  <Wallet className="w-4 h-4 text-secondary" />
                </div>
                <p className="font-mono text-2xl font-bold">{agent.stx_balance?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Gas for transactions</p>
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="agent-trust-score">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Trust Score</span>
                  <Shield className="w-4 h-4 text-green-400" />
                </div>
                <p className="font-mono text-2xl font-bold">{agent.trust_score}/100</p>
                <Progress value={agent.trust_score} className="h-1 mt-2" />
              </CardContent>
            </Card>

            <Card className="glass-card" data-testid="agent-yield-earned">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {getDominantDefiYield() 
                      ? `Total ${getDominantDefiYield()?.asset} Yield Earned`
                      : 'Total Yield Earned'
                    }
                  </span>
                  <div className="flex items-center gap-2">
                    {getDominantDefiYield() && (
                      <button
                        onClick={toggleUsd}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
                        data-testid="agent-yield-usd-toggle"
                        title="Toggle between token and USD"
                      >
                        {showYieldUsd ? "USD" : getDominantDefiYield()?.asset}
                      </button>
                    )}
                    <TrendingUp className="w-4 h-4 text-primary" />
                  </div>
                </div>
                {getDominantDefiYield() ? (
                  <>
                    <p className="font-mono text-2xl font-bold text-green-400">
                      {showYieldUsd && yieldAssetUsd(getDominantDefiYield()?.asset)
                        ? `+$${(getDominantDefiYield().yield * yieldAssetUsd(getDominantDefiYield().asset)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : `+${getDominantDefiYield()?.yield?.toLocaleString(undefined, { maximumFractionDigits: 6 })}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {showYieldUsd && yieldAssetUsd(getDominantDefiYield()?.asset)
                        ? `${getDominantDefiYield()?.asset} yield shown in USD`
                        : `${getDominantDefiYield()?.asset} earned via ${getDominantDefiYield()?.protocol}`}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-mono text-2xl font-bold text-green-400">+{agent.total_yield_earned?.toFixed(8)}</p>
                    <p className="text-xs text-muted-foreground">sBTC earned</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Portfolio & Chart */}
            <div className="lg:col-span-2 space-y-6">
              {/* Portfolio Chart */}
              <Card className="glass-card">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-xl">Portfolio Performance</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={portfolioHistory}>
                        <defs>
                          <linearGradient id="colorPortfolio" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#FF9F43" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#FF9F43" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                        <XAxis 
                          dataKey="time" 
                          stroke="#71717A"
                          tick={{ fill: '#71717A', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                        />
                        <YAxis 
                          stroke="#71717A"
                          tick={{ fill: '#71717A', fontSize: 11, fontFamily: 'JetBrains Mono' }}
                          tickFormatter={(value) => value.toFixed(4)}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            background: 'rgba(10, 10, 10, 0.95)', 
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            fontFamily: 'JetBrains Mono'
                          }}
                          formatter={(value) => [value.toFixed(8) + ' sBTC', 'Balance']}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#FF9F43" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorPortfolio)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* AI Decision Panel */}
              <Card className="glass-card border-secondary/30">
                <CardHeader className="border-b border-border">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Brain className="w-5 h-5 text-secondary" />
                      AI Decision Engine
                    </CardTitle>
                    <Badge variant="outline" className="font-mono text-xs">Gemini 3 Flash</Badge>
                  </div>
                  <CardDescription>Request autonomous trading decisions from the AI</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Market Context (optional)</label>
                    <Textarea
                      placeholder="Add any specific market context or questions for the AI..."
                      value={aiContext}
                      onChange={(e) => setAiContext(e.target.value)}
                      className="bg-muted/50 border-border min-h-[80px]"
                      data-testid="ai-context-input"
                    />
                  </div>
                  <Button 
                    onClick={handleRequestDecision}
                    disabled={deciding}
                    className="w-full bg-secondary/20 text-secondary border border-secondary/30 hover:bg-secondary/30"
                    data-testid="request-decision-btn"
                  >
                    {deciding ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Thinking...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Request AI Decision
                      </>
                    )}
                  </Button>

                  {lastDecision && (
                    <div className="mt-4 p-4 rounded-lg bg-muted/30 border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Latest Decision</span>
                        <Badge className={`${
                          lastDecision.action_type === 'hold' ? 'bg-yellow-500/20 text-yellow-400' :
                          lastDecision.action_type === 'deposit' ? 'bg-green-500/20 text-green-400' :
                          'bg-primary/20 text-primary'
                        }`}>
                          {lastDecision.action_type?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{lastDecision.decision}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Confidence: {(lastDecision.confidence * 100).toFixed(0)}%</span>
                        {lastDecision.suggested_protocol && (
                          <span>Protocol: {lastDecision.suggested_protocol}</span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Positions */}
              <Card className="glass-card">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-xl">Active Positions</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {portfolio?.active_positions?.map((position, index) => (
                      <div 
                        key={index}
                        className="p-4 rounded-lg bg-muted/30 border border-border"
                        data-testid={`position-${index}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{position.protocol}</span>
                          <span className="text-xs font-mono text-green-400">{position.apy}% APY</span>
                        </div>
                        <p className="font-mono text-lg font-bold">{position.amount?.toFixed(6)} sBTC</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Activity & Info */}
            <div className="space-y-6">
              {/* Addresses */}
              <Card className="glass-card">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-lg">Agent Identity</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">BTC Address</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs bg-muted/50 px-2 py-1 rounded flex-1 truncate font-mono">
                        {agent.wallet_address}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(agent.wallet_address)}
                        data-testid="copy-btc-address"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">STX Address</label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="text-xs bg-muted/50 px-2 py-1 rounded flex-1 truncate font-mono">
                        {agent.stx_address}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(agent.stx_address)}
                        data-testid="copy-stx-address"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Created</label>
                    <p className="text-sm font-mono mt-1">
                      {new Date(agent.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Peg Status */}
              <Card className={`glass-card ${pegStatus?.status !== 'stable' ? 'border-yellow-500/50' : ''}`}>
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-400" />
                    Peg Watchdog
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">sBTC/BTC Ratio</span>
                    <span className={`font-mono font-bold ${pegStatus?.status === 'stable' ? 'text-green-400' : 'text-yellow-400'}`}>
                      {pegStatus?.ratio?.toFixed(4)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge className={pegStatus?.status === 'stable' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                      {pegStatus?.status?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Alert Threshold</span>
                    <span className="font-mono text-sm">{agent.config?.peg_deviation_threshold}%</span>
                  </div>
                </CardContent>
              </Card>

              {/* Activity Feed */}
              <Card className="glass-card">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-heading text-lg">Activity Feed</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[350px]">
                    {activities.length === 0 ? (
                      <div className="p-8 text-center">
                        <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                        <p className="text-sm text-muted-foreground">No activities yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {activities.map((activity, index) => (
                          <div 
                            key={activity.id || index}
                            className="p-4 hover:bg-muted/30 transition-colors"
                            data-testid={`agent-activity-${index}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActivityColor(activity.type)}`}>
                                {getActivityIcon(activity.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{activity.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2">{activity.description}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Clock className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {new Date(activity.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Real DeFi Section */}
          <div className="mt-8">
            <Card className="glass-card border-primary/30">
              <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Coins className="w-5 h-5 text-primary" />
                    Real DeFi Integration
                  </CardTitle>
                  <Badge variant="outline" className="font-mono text-xs border-green-500/50 text-green-400">
                    MAINNET
                  </Badge>
                </div>
                <CardDescription>
                  Deposit funds into real DeFi protocols (Zest, StackingDAO, ALEX, Hermetica, Bitflow, Velar DEX) to earn actual yield
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <DeFiManager agentId={id} agentName={agent.name} />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
